import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import { mediaApi } from '@/api/mediaWrapper';
import type { MediaAssetResponse, MediaTagResponse } from 'we-tree-api-client/v2';

import './media-tag-modal.css';

export interface MediaTagModalProps {
  familyId: string;
  asset: MediaAssetResponse;
  availableTags: MediaTagResponse[];
  isOpen: boolean;
  onClose: () => void;
  onTagsChange: () => void;
}

const MediaTagModal: React.FC<MediaTagModalProps> = ({
  familyId,
  asset,
  availableTags,
  isOpen,
  onClose,
  onTagsChange
}) => {
  const { t } = useTranslation('forms');
  const { t: tCommon } = useTranslation('common');
  const [newTagName, setNewTagName] = useState('');
  const [isCreatingTag, setIsCreatingTag] = useState(false);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) onClose();
    },
    [onClose]
  );

  const assetTagNames = new Set(asset.tags ?? []);
  const hasTag = (tagId: string) => {
    const tag = availableTags.find((item) => item.id === tagId);
    return tag ? assetTagNames.has(tag.name) : false;
  };

  const handleAddTag = async (tagId: string) => {
    const result = await mediaApi.addTagToMediaAsset(familyId, asset.id, tagId);
    if (result.success) onTagsChange();
  };

  const handleRemoveTag = async (tagId: string) => {
    const result = await mediaApi.removeTagFromMediaAsset(familyId, asset.id, tagId);
    if (result.success) onTagsChange();
  };

  const handleCreateAndAssignTag = async () => {
    const name = newTagName.trim();
    if (!name || isCreatingTag) return;
    setIsCreatingTag(true);
    const createResult = await mediaApi.createMediaTag(familyId, name);
    if (createResult.success && createResult.data) {
      const addResult = await mediaApi.addTagToMediaAsset(familyId, asset.id, createResult.data.id);
      if (addResult.success) {
        onTagsChange();
        setNewTagName('');
      }
    }
    setIsCreatingTag(false);
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="v01-modal-overlay v01-modal-overlay--centered">
          <Dialog.Content className="v01-modal media-tag-modal__shell" aria-label={t('mediaTags.editAria')}>
            <div className="media-tag-modal__header">
              <h2 className="v01-modal__title">{t('mediaTags.editTitle')}</h2>
              <button type="button" className="v01-modal__close" onClick={onClose} aria-label={tCommon('close')}>
                ×
              </button>
            </div>

            <div className="media-tag-modal__body">
              <p className="media-tag-modal__hint">{t('mediaTags.hint')}</p>

              {availableTags.length > 0 && (
                <div className="media-tag-modal__tag-list">
                  <span className="media-tag-modal__tag-list-label">{t('actions.tags')}</span>
                  {availableTags.map((tag) => (
                    <label key={tag.id} className="media-tag-modal__tag-item">
                      <input
                        type="checkbox"
                        checked={hasTag(tag.id)}
                        onChange={(e) => {
                          if (e.target.checked) handleAddTag(tag.id);
                          else handleRemoveTag(tag.id);
                        }}
                      />
                      <span>{tag.name}</span>
                    </label>
                  ))}
                </div>
              )}

              <div className="media-tag-modal__create">
                <label htmlFor="media-tag-modal-new-tag" className="media-tag-modal__create-label">
                  {t('mediaTags.createNew')}
                </label>
                <div className="media-tag-modal__create-row">
                  <input
                    id="media-tag-modal-new-tag"
                    type="text"
                    className="media-tag-modal__create-input"
                    placeholder={t('mediaTags.namePlaceholder')}
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateAndAssignTag()}
                    disabled={isCreatingTag}
                  />
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={handleCreateAndAssignTag}
                    disabled={!newTagName.trim() || isCreatingTag}
                  >
                    {isCreatingTag ? '…' : t('mediaTags.createAndAdd')}
                  </button>
                </div>
              </div>
            </div>

            <div className="media-tag-modal__footer">
              <button type="button" className="btn btn--primary" onClick={onClose}>
                {tCommon('done')}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default MediaTagModal;
